/**
 * DaddyTalkAnnouncementBar — thin cobalt strip at the very top of the
 * landing page announcing DaddyTalk's launch. Dismissable via
 * localStorage so returning visitors don't see it forever.
 *
 * Design: cobalt bg, white text, subtle Uncle D avatar peeking in,
 * one primary CTA + X to dismiss. Auto-hides 30 days after first view.
 */
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { X, ArrowRight } from "@phosphor-icons/react";
import { useAuth } from "@/context/AuthContext";

const STORAGE_KEY = "daddytalk-announce-dismissed-v1";
const AUTO_HIDE_AFTER_DAYS = 30;
const FIRST_SEEN_KEY = "daddytalk-announce-first-seen";
// V1.6.4 — Staff don't need the candidate-facing marketing strip.
const STAFF_ROLES = new Set(["recruiter", "admin", "hiring_manager", "superadmin"]);

export default function DaddyTalkAnnouncementBar() {
    const [visible, setVisible] = useState(false);
    const { user } = useAuth();

    useEffect(() => {
        // Only run on client
        try {
            if (localStorage.getItem(STORAGE_KEY) === "1") return;
            const firstSeen = localStorage.getItem(FIRST_SEEN_KEY);
            const now = Date.now();
            if (!firstSeen) {
                localStorage.setItem(FIRST_SEEN_KEY, String(now));
            } else if (now - Number(firstSeen) > AUTO_HIDE_AFTER_DAYS * 24 * 60 * 60 * 1000) {
                return;
            }
            setVisible(true);
        } catch {
            setVisible(true);
        }
    }, []);

    const dismiss = () => {
        try { localStorage.setItem(STORAGE_KEY, "1"); } catch (_) { /* ignore */ }
        setVisible(false);
    };

    if (!visible) return null;
    // V2.1.1 — Suppress on paid ad landings; only the ad's CTA should compete
    // for attention on the /quick-apply/* funnel.
    if (typeof window !== "undefined") {
        const p = window.location.pathname || "";
        if (p.startsWith("/quick-apply") || p.startsWith("/apply/")) return null;
    }
    // V1.7.1 — Don't render until auth is resolved to avoid the
    // "shows for a second then hides" flicker on staff sessions.
    if (user === null) return null;
    // V1.6.4 — hide from staff (they use /messages, not the marketing bar).
    if (user && STAFF_ROLES.has(user.role)) return null;

    return (
        <div
            data-testid="daddytalk-announce-bar"
            className="w-full bg-cobalt text-white text-xs sm:text-sm py-2.5 px-4 relative z-[70]"
        >
            <div className="max-w-6xl mx-auto flex items-center justify-center gap-3 pr-8">
                <img
                    src="/mascot/daddytalk/avatar_hello.png"
                    alt=""
                    className="hidden sm:block w-7 h-7 rounded-full border-2 border-white shrink-0"
                    aria-hidden="true"
                />
                <span className="font-semibold">
                    NEW · Chat directly with recruiters on{" "}
                    <span className="font-black">DaddyTalk</span> — no more black-hole applications
                </span>
                <Link
                    to="/daddytalk"
                    data-testid="daddytalk-announce-cta"
                    className="hidden sm:inline-flex items-center gap-1 bg-white text-cobalt font-black uppercase tracking-wider text-[10px] px-2.5 py-1 rounded hover:bg-cream transition-colors"
                >
                    Try it
                    <ArrowRight size={10} weight="bold" />
                </Link>
            </div>
            <button
                type="button"
                onClick={dismiss}
                data-testid="daddytalk-announce-dismiss"
                aria-label="Dismiss announcement"
                className="absolute right-3 top-1/2 -translate-y-1/2 text-white/80 hover:text-white transition-colors"
            >
                <X size={14} weight="bold" />
            </button>
        </div>
    );
}
